import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Asset,
  ContentfulEmbeddedAsset,
  ContentfulParagraph,
  ContentfulText,
  ContentfulLink,
  ContentfulList,
  Page,
} from "../types";

import config from "../config/index.json";
import Header from "./Header";
import Footer from "./Footer";

interface ContentfulResponse {
  items: Page[];
  includes: {
    Asset: Asset[];
  };
}

AOS.init({
  offset: 50,
  duration: 120,
  easing: "ease",
  delay: 0,
});

const CONTENTFUL_SPACE_ID = "qitjy5hyl5up";
const CONTENTFUL_ACCESS_TOKEN = "0PL0qCiNnT4GhSRGoDG8E69TrtEvJwipPHfutAMhECU";
const contentfulEndpoint = `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/master/entries?access_token=${CONTENTFUL_ACCESS_TOKEN}&content_type=page`;

export const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const handleLinkClick = () => {
  window.scrollTo(0, 0); // Przewijanie do góry strony
};

const SinglePage = () => {
  const { pageSlug } = useParams();
  const [selectedPage, setSelectedPage] = useState<Page | undefined>(undefined);
  const [assets, setAssets] = useState<Asset[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [suggestedPageTitle, setSuggestedPageTitle] = useState<
    string | undefined
  >(undefined);
  const { images } = config;
  const { arrow2 } = images;

  useEffect(() => {
    (async () => {
      if (selectedPage?.fields.suggestedPage.sys.id) {
        try {
          const suggestedPageId = selectedPage?.fields.suggestedPage.sys.id;
          const response = await fetch(
            `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/master/entries/${suggestedPageId}?access_token=${CONTENTFUL_ACCESS_TOKEN}`
          );
          const data = await response.json();
          setSuggestedPageTitle(data.fields.title);
        } catch (err) {
          console.error("Error fetching suggested page title:", err);
        }
      }
    })();
  }, [selectedPage]);

  useEffect(() => {
    (async () => {
      await fetch(contentfulEndpoint)
        .then((res) => {
          return res.json();
        })
        .then((res) => {
          setSelectedPage(
            (res as unknown as ContentfulResponse).items.find(
              (page: Page) => slugify(page.fields.title) == pageSlug
            )
          );
          setAssets((res as unknown as ContentfulResponse).includes.Asset);
        })
        .catch((err) => {
          console.log(err);
          setError("Failed to fetch pages. Please try again later.");
        });
    })();
  }, [pageSlug]);

  if (!setSelectedPage) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Header />
      {selectedPage && assets && suggestedPageTitle && (
        <>
          <section
            id="hero"
            className="pt-72 pb-36"
            style={{
              background: "#" + selectedPage.fields.backgroundColor,
              color: "#" + selectedPage.fields.primaryColor,
            }}
          >
            <div className="flex flex-col md:flex-row px-8 md:px-16 items-center h-full">
              <div className="basis-8/12 md:pr-16 text-center md:text-left">
                <h3
                  data-aos="fade-down"
                  className="font-300 text-white text-5xl max-w-[480px] leading-snug pb-16"
                >
                  {selectedPage.fields.summary.content.map((content) => {
                    if (content.nodeType === "paragraph") {
                      const paragraphContent = (content as ContentfulParagraph)
                        .content;

                      // Collect and join the content values
                      const paragraphText = paragraphContent
                        .map((node) => {
                          if (node.nodeType === "text") {
                            return (node as ContentfulText).value;
                          }
                          return "";
                        })
                        .join("");

                      return paragraphText; // Return the joined content
                    }
                    return ""; // Handle other content types or return an empty string
                  })}
                </h3>
              </div>
            </div>

            <div className="flex flex-col md:flex-row px-8 md:px-16 items-center h-full">
              <div className="basis-8/12 md:pr-16 text-center md:text-left relative">
                <h1
                  id="title"
                  data-aos="fade-down"
                  className="text-white text-center md:text-left text-2xl uppercase font-semibold pb-4 md:leading-tight absolute"
                >
                  {selectedPage.fields.title}
                </h1>
              </div>
            </div>

            <div className="contentArea flex flex-col md:flex-row items-center w-full">
              <div className="text-center md:text-left">
                {selectedPage.fields.body.content.map(
                  (content, index, array) => {
                    //console.log(selectedPage.fields.body.content);

                    let renderedContent = null;

                    if (content.nodeType === "paragraph") {
                      const paragraphContent = (content as ContentfulParagraph)
                        .content;
                      renderedContent = paragraphContent.map(
                        (node, nodeIndex) => {
                          if (node.nodeType === "text") {
                            if (
                              node.marks.some((mark) => mark.type === "bold")
                            ) {
                              return (
                                <span key={nodeIndex}>
                                  <strong>
                                    {(node as ContentfulText).value}
                                  </strong>
                                </span>
                              );
                            } else {
                              return (
                                <span key={nodeIndex}>
                                  {(node as ContentfulText).value}
                                </span>
                              );
                            }
                          } else if (node.nodeType === "hyperlink") {
                            const linkContent = (node as ContentfulLink)
                              .content;
                            let linkText = "";

                            if (Array.isArray(linkContent)) {
                              linkText = linkContent
                                .map((textNode) => {
                                  if (textNode.nodeType === "text") {
                                    return (textNode as ContentfulText).value;
                                  }
                                  return "";
                                })
                                .join("");
                            }

                            const linkUri = (node as ContentfulLink).data.uri;
                            //console.log(linkText);

                            return (
                              <a key={nodeIndex} href={linkUri} target="_blank">
                                {linkText}
                              </a>
                            );
                          } else {
                            return null;
                          }
                        }
                      );
                    } else if (content.nodeType === "blockquote") {
                      return (
                        <blockquote
                          key={index}
                          data-aos="fade-down"
                          style={{
                            color: "#" + selectedPage.fields.secondaryColor,
                          }}
                        >
                          {(content as ContentfulText).value}
                        </blockquote>
                      );
                    } else if (content.nodeType === "heading-1") {
                      return (
                        <h3 key={index} data-aos="fade-down">
                          {(content as ContentfulText).value}
                        </h3>
                      );
                    } else if (content.nodeType === "embedded-asset-block") {
                      // Check if the previous or next node is not embedded-asset-block
                      const isStart =
                        index === 0 ||
                        array[index - 1]?.nodeType !== "embedded-asset-block";
                      const isEnd =
                        index === array.length - 1 ||
                        array[index + 1]?.nodeType !== "embedded-asset-block";

                      // Determine the class based on the position and surrounding nodes
                      let imgNumber = 1;
                      if (!isStart) {
                        let prevIndex = index - 1;
                        while (
                          prevIndex >= 0 &&
                          array[prevIndex]?.nodeType === "embedded-asset-block"
                        ) {
                          imgNumber++;
                          prevIndex--;
                        }
                      }
                      const divClass = `img${imgNumber
                        .toString()
                        .padStart(2, "0")}`;

                      return (
                        <div
                          data-aos="fade-down"
                          className={`gallery ${divClass}`}
                          key={index}
                        >
                          <img
                            src={
                              assets.find(
                                (asset) =>
                                  asset.sys.id ===
                                  (content as ContentfulEmbeddedAsset).data
                                    .target.sys.id
                              )?.fields.file.url
                            }
                            alt=""
                          />
                        </div>
                      );
                    } else if (content.nodeType === "unordered-list") {
                      const ulContent = (content as ContentfulList).content;
                      return (
                        <ul data-aos="fade-down" key={index}>
                          {ulContent.map((listItem, listItemIndex) => {
                            if (
                              "content" in listItem &&
                              Array.isArray(listItem.content) &&
                              listItem.content.length > 0 &&
                              "content" in listItem.content[0] &&
                              Array.isArray(listItem.content[0].content) &&
                              listItem.content[0].content.length > 0 &&
                              "value" in listItem.content[0].content[0]
                            ) {
                              const value = (
                                listItem.content[0].content[0] as ContentfulText
                              ).value;
                              //console.log(value);
                              return <li key={listItemIndex}>{value}</li>;
                            } else {
                              // Handle cases where the structure doesn't match the expected pattern
                              return null;
                            }
                          })}
                        </ul>
                      );
                    }

                    return <p data-aos="fade-down">{renderedContent}</p>;
                  }
                )}
              </div>
            </div>

            <div
              data-aos="fade-left"
              className="pageFoot flex place-content-end pt-36 pb-12 px-24"
            >
              <Link
                to={"/" + slugify(suggestedPageTitle)}
                className="flex text-white text-4xl"
                onClick={handleLinkClick}
              >
                Continua con{" "}
                <strong className="font-semibold">{suggestedPageTitle}</strong>
                <img src={arrow2} className="w-8 ml-6" />
              </Link>
            </div>
          </section>
        </>
      )}
      <Footer />
    </div>
  );
};

export default SinglePage;
